import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { AdminWorldDetail, AdminWorldState } from '../../../lib/api';

interface WorldHeaderProps {
  world: AdminWorldDetail;
  state: AdminWorldState | null;
  versions: Array<{ id: string; version: number; updatedAt: string }>;
  currentVersion: number | null;
  onVersionChange: (version: number | null) => void;
  onThumbnailClick?: () => void;
}

export function WorldHeader({
  world,
  state,
  versions,
  currentVersion,
  onVersionChange,
  onThumbnailClick,
}: WorldHeaderProps) {
  const thumbnailUrl = state?.thumbnailUrl ?? world.latestState?.thumbnailUrl;

  // Sort versions descending (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);
  const activeVersion = currentVersion ?? state?.version ?? sortedVersions[0]?.version;
  const currentIndex = sortedVersions.findIndex((v) => v.version === activeVersion);

  const canGoNewer = currentIndex > 0;
  const canGoOlder = currentIndex < sortedVersions.length - 1;

  const goNewer = () => {
    if (canGoNewer) {
      onVersionChange(sortedVersions[currentIndex - 1].version);
    }
  };

  const goOlder = () => {
    if (canGoOlder) {
      onVersionChange(sortedVersions[currentIndex + 1].version);
    }
  };

  return (
    <div className="p-4 border-b border-stone/30">
      {/* Thumbnail */}
      <div className="flex items-start gap-3 mb-4">
        {thumbnailUrl ? (
          <button
            onClick={onThumbnailClick}
            className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-rune/50 transition-all"
          >
            <img
              src={thumbnailUrl}
              alt="World thumbnail"
              className="h-full w-full object-contain bg-parchment/95"
            />
          </button>
        ) : (
          <div className="h-16 w-16 rounded-lg bg-stone/30 flex items-center justify-center text-xs text-mist flex-shrink-0">
            —
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs text-mist/70 mb-0.5">
            <Link to={`/admin/users/${world.user.id}`} className="hover:text-rune">
              {world.user.email}
            </Link>
          </div>
          <div className="font-mono text-sm text-parchment truncate" title={world.inboundToken}>
            {world.inboundToken}
          </div>
        </div>
      </div>

      {/* Day Index */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-mist uppercase tracking-wide">Day</span>
        <span className="text-lg font-display text-parchment">{world.dayIndex}</span>
      </div>

      {/* Version Selector */}
      {versions.length > 0 && (
        <div>
          <label className="block text-xs text-mist uppercase tracking-wide mb-1.5">
            State Version
          </label>
          <div className="flex gap-1">
            <select
              value={currentVersion ?? state?.version ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  onVersionChange(null);
                } else {
                  onVersionChange(parseInt(value, 10));
                }
              }}
              className="flex-1 min-w-0 px-2 py-1.5 bg-night border border-stone/30 rounded text-parchment text-sm focus:outline-none focus:border-rune"
            >
              {sortedVersions.map((v) => (
                <option key={v.id} value={v.version}>
                  v{v.version} - {formatDateShort(v.updatedAt)}
                </option>
              ))}
            </select>
            <div className="flex flex-col">
              <button
                onClick={goNewer}
                disabled={!canGoNewer}
                className="px-1.5 py-0.5 bg-night border border-stone/30 rounded-t text-mist hover:text-parchment hover:bg-stone/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Newer version"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={goOlder}
                disabled={!canGoOlder}
                className="px-1.5 py-0.5 bg-night border border-t-0 border-stone/30 rounded-b text-mist hover:text-parchment hover:bg-stone/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Older version"
              >
                <ChevronDown size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="bg-void/50 rounded p-2">
          <div className="text-mist/70">Timezone</div>
          <div className="text-parchment truncate" title={world.timezone}>
            {world.timezone}
          </div>
        </div>
        <div className="bg-void/50 rounded p-2">
          <div className="text-mist/70">Delivery</div>
          <div className="text-parchment">{world.deliveryTime}</div>
        </div>
      </div>
    </div>
  );
}

function formatDateShort(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
